import config from "@/config";
import { getFormEmbedUrlForRegion } from "@/config/regionalPreviewUrl";
import { useAppStore } from "@/store/app";
import { isHexColor } from "@/util/color";
import {
  buildEmbedCode,
  getEmbedScriptBaseUrl,
  type EmbedLayout,
  type EmbedLayoutId,
  type EmbedSettings,
} from "@/util/embedCode";
import clamp from "lodash/clamp";
import { computed, reactive, readonly, watch } from "vue";

type NumericLayoutKey =
  | "formWidth"
  | "formHeight"
  | "fontSize"
  | "cornerRadius";
type TextLayoutKey = "minimizedTitle" | "buttonColor" | "buttonIcon";
type SelectLayoutKey = "buttonType" | "buttonSize";
export type AdvancedFieldKey =
  | "showOnScrolling"
  | "showAfter"
  | "activateOnVisit"
  | "deactivateAfter";
type StoredEmbedSettings = {
  selectedLayout?: EmbedLayout;
  selectedTriggerType?: string;
  selectedActivation?: string;
  selectedDeactivation?: string;
  checkForCookieConsent?: boolean;
  cookieConsentProvider?: string;
  fields?: Record<string, string>;
};

const buttonDefaults = {
  minimizedTitle: "Contact us",
  buttonColor: "#101828",
  buttonType: "text" as const,
  buttonSize: "medium" as const,
  buttonIcon: "chat",
  cornerRadius: { tl: 8, tr: 8, bl: 8, br: 8 },
  fontSize: 16,
};
// Legacy embeds reserve this space for the agency branding element.
const AGENCY_BRANDING_HEIGHT_PX = 126;
const MAX_CORNER_RADIUS_PX = 50;
const defaults: Record<EmbedLayoutId, EmbedLayout> = {
  STICKY_SIDEBAR: {
    id: "STICKY_SIDEBAR",
    allowMinimize: true,
    isLeftAligned: true,
    isRightAligned: false,
    ...buttonDefaults,
  },
  POLITE_SLIDE_IN: {
    id: "POLITE_SLIDE_IN",
    allowMinimize: true,
    isLeftAligned: false,
    isRightAligned: true,
    ...buttonDefaults,
  },
  POPUP: { id: "POPUP", ...buttonDefaults },
  INLINE: { id: "INLINE" },
};

const clampLayoutValue = (value: number, minimum: number, maximum: number) =>
  clamp(Math.round(Number(value) || 0), minimum, maximum);

const clampCornerRadius = (
  corners: NonNullable<EmbedLayout["cornerRadius"]>
) => ({
  tl: clampLayoutValue(corners.tl, 0, MAX_CORNER_RADIUS_PX),
  tr: clampLayoutValue(corners.tr, 0, MAX_CORNER_RADIUS_PX),
  bl: clampLayoutValue(corners.bl, 0, MAX_CORNER_RADIUS_PX),
  br: clampLayoutValue(corners.br, 0, MAX_CORNER_RADIUS_PX),
});

export const getDefaultEmbedFormWidth = (width: number) =>
  clampLayoutValue(width, 100, 2000);

export function useEmbedSettings() {
  const store = useAppStore();
  const defaultFormWidth = computed(() =>
    getDefaultEmbedFormWidth(store.formStyle.width)
  );
  const saved = (store.integrate.formLayoutSettings ??
    {}) as StoredEmbedSettings;
  const initialId = (store.integrateEmbedLayoutId ||
    saved.selectedLayout?.id ||
    "STICKY_SIDEBAR") as EmbedLayoutId;
  const layoutDrafts = reactive<Record<EmbedLayoutId, EmbedLayout>>({
    STICKY_SIDEBAR: { ...defaults.STICKY_SIDEBAR },
    POLITE_SLIDE_IN: { ...defaults.POLITE_SLIDE_IN },
    POPUP: { ...defaults.POPUP },
    INLINE: { ...defaults.INLINE },
  });
  if (saved.selectedLayout?.id) {
    layoutDrafts[saved.selectedLayout.id] = {
      ...layoutDrafts[saved.selectedLayout.id],
      ...saved.selectedLayout,
      formWidth:
        saved.selectedLayout.formWidth === store.formStyle.width
          ? undefined
          : saved.selectedLayout.formWidth,
      cornerRadius: saved.selectedLayout.cornerRadius
        ? clampCornerRadius(saved.selectedLayout.cornerRadius)
        : layoutDrafts[saved.selectedLayout.id].cornerRadius,
    };
  }
  const fields = reactive<Record<AdvancedFieldKey, string>>({
    showOnScrolling: saved.fields?.showOnScrolling || "",
    showAfter: saved.fields?.showAfter || "",
    activateOnVisit: saved.fields?.activateOnVisit || "",
    deactivateAfter: saved.fields?.deactivateAfter || "",
  });
  const triggerType = saved.selectedTriggerType || "alwaysShow";
  const activationType = saved.selectedActivation || "alwaysActivated";
  const deactivationType = saved.selectedDeactivation || "neverDeactivate";
  const settings = reactive<EmbedSettings>({
    layout: layoutDrafts[initialId],
    triggerType,
    triggerValue: fields[triggerType as AdvancedFieldKey] || "",
    activationType,
    activationValue: fields[activationType as AdvancedFieldKey] || "",
    deactivationType,
    deactivationValue: fields[deactivationType as AdvancedFieldKey] || "",
    checkForCookieConsent: saved.checkForCookieConsent ?? true,
    cookieConsentProvider: saved.cookieConsentProvider || "auto",
    modalHeight: layoutDrafts[initialId].formHeight,
  });
  store.integrateEmbedLayoutId = null;

  const selectLayout = (id: EmbedLayoutId) => {
    settings.layout = layoutDrafts[id];
    settings.modalHeight = settings.layout.formHeight;
  };
  watch(
    () => store.integrateEmbedLayoutId,
    (id) => {
      if (id && Object.prototype.hasOwnProperty.call(layoutDrafts, id)) {
        selectLayout(id as EmbedLayoutId);
        store.integrateEmbedLayoutId = null;
      }
    }
  );
  const setAllowMinimize = (value: boolean) => {
    settings.layout.allowMinimize = value;
  };
  const setPosition = (position: "left" | "right") => {
    settings.layout.isLeftAligned = position === "left";
    settings.layout.isRightAligned = position === "right";
  };
  const setLayoutText = (key: TextLayoutKey, value: string) => {
    settings.layout[key] = value;
  };
  const setLayoutSelect = (key: SelectLayoutKey, value: string) => {
    if (key === "buttonType" && ["text", "icon", "textIcon"].includes(value)) {
      settings.layout.buttonType = value as NonNullable<
        EmbedLayout["buttonType"]
      >;
    }
    if (key === "buttonSize" && ["small", "medium", "large"].includes(value)) {
      settings.layout.buttonSize = value as NonNullable<
        EmbedLayout["buttonSize"]
      >;
    }
  };
  const setNumericLayoutValue = (
    key: NumericLayoutKey,
    value: number | null | undefined
  ) => {
    if (key === "formWidth" || key === "formHeight") {
      const next = value == null || !Number.isFinite(value) ? undefined : value;
      settings.layout[key] = next;
      if (key === "formHeight") settings.modalHeight = next;
      return;
    }
    if (key === "cornerRadius") {
      const next = value == null || !Number.isFinite(value) ? 0 : value;
      settings.layout.cornerRadius = { tl: next, tr: next, bl: next, br: next };
    } else {
      settings.layout.fontSize = clampLayoutValue(value, 8, 100);
    }
  };
  const commitDimension = (key: "formWidth" | "formHeight") => {
    const value = settings.layout[key];
    const clamped =
      value === undefined ? undefined : clampLayoutValue(value, 100, 2000);
    const next =
      key === "formWidth" && clamped === store.formStyle.width
        ? undefined
        : clamped;
    settings.layout[key] = next;
    if (key === "formHeight") settings.modalHeight = next;
  };
  const setCornerValue = (
    corner: keyof NonNullable<EmbedLayout["cornerRadius"]>,
    value: number
  ) => {
    const next = Number.isFinite(value) ? value : 0;
    settings.layout.cornerRadius = {
      ...(settings.layout.cornerRadius || { tl: 0, tr: 0, bl: 0, br: 0 }),
      [corner]: next,
    };
  };
  const commitCornerValue = (
    corner: keyof NonNullable<EmbedLayout["cornerRadius"]>,
    linked: boolean
  ) => {
    const corners = settings.layout.cornerRadius || { tl: 0, tr: 0, bl: 0, br: 0 };
    const next = clampLayoutValue(corners[corner], 0, MAX_CORNER_RADIUS_PX);
    settings.layout.cornerRadius = linked
      ? { tl: next, tr: next, bl: next, br: next }
      : { ...corners, [corner]: next };
  };

  const iframeHeight = computed(() => {
    if (settings.layout.id === "INLINE")
      return store.embedFrameHeights.theFormBuilder;
    const height = store.embedFrameHeights.formBuilder;
    if (height === undefined) return undefined;
    return height + (store.formStyle?.acBranding ? AGENCY_BRANDING_HEIGHT_PX : 0);
  });
  const embedCode = computed(() =>
    buildEmbedCode({
      ...settings,
      layout: settings.layout.cornerRadius
        ? { ...settings.layout, cornerRadius: clampCornerRadius(settings.layout.cornerRadius) }
        : settings.layout,
    }, {
      embedUrl: store.integrateShareUrl,
      scriptBaseUrl: getEmbedScriptBaseUrl({
        baseUrl: store.integrate.baseUrl,
        fallbackScriptBaseUrl: getFormEmbedUrlForRegion(store.regionCode),
        isWhiteLabelled: store.integrate.isWhiteLabelled,
        environment: process.env.NODE_ENV,
      }),
      widgetId: store.integrateWidget.id,
      formName: store.builder?.name || "",
      borderRadius: store.formStyle?.border?.radius || 0,
      iframeHeight: iframeHeight.value,
      formStyleWidth: store.formStyle.width,
    })
  );
  const isTriggerValid = computed(() => {
    const triggerValue = Number(settings.triggerValue);
    if (settings.triggerType === "showOnScrolling") {
      return triggerValue >= 1 && triggerValue <= 100;
    }
    return settings.triggerType !== "showAfter" || triggerValue >= 1;
  });
  const isActivationValid = computed(
    () =>
      settings.activationType !== "activateOnVisit" ||
      Number(settings.activationValue) >= 1
  );
  const isDeactivationValid = computed(
    () =>
      settings.deactivationType !== "deactivateAfter" ||
      Number(settings.deactivationValue) >= 1
  );
  const isLayoutValid = computed(
    () =>
      settings.layout.buttonColor === undefined ||
      isHexColor(settings.layout.buttonColor)
  );
  const isValid = computed(() => {
    return (
      isTriggerValid.value &&
      isActivationValid.value &&
      isDeactivationValid.value &&
      isLayoutValid.value
    );
  });
  const setTrigger = (type: string, value?: string) => {
    if (value !== undefined && type in fields)
      fields[type as AdvancedFieldKey] = value;
    settings.triggerType = type;
    settings.triggerValue = fields[type as AdvancedFieldKey] || "";
  };
  const setActivation = (type: string, value?: string) => {
    if (value !== undefined && type in fields)
      fields[type as AdvancedFieldKey] = value;
    settings.activationType = type;
    settings.activationValue = fields[type as AdvancedFieldKey] || "";
  };
  const setDeactivation = (type: string, value?: string) => {
    if (value !== undefined && type in fields)
      fields[type as AdvancedFieldKey] = value;
    settings.deactivationType = type;
    settings.deactivationValue = fields[type as AdvancedFieldKey] || "";
  };
  const setCookieConsent = (
    enabled: boolean,
    provider = settings.cookieConsentProvider
  ) => {
    settings.checkForCookieConsent = enabled;
    settings.cookieConsentProvider = provider;
  };
  const toStoredSettings = () => {
    const { formWidth, ...layout } = settings.layout;
    const normalizedLayout = layout.cornerRadius
      ? { ...layout, cornerRadius: clampCornerRadius(layout.cornerRadius) }
      : layout;
    return {
      selectedLayout:
        formWidth === undefined || formWidth === store.formStyle.width
          ? normalizedLayout
          : { ...normalizedLayout, formWidth },
      selectedTriggerType: settings.triggerType,
      selectedActivation: settings.activationType,
      selectedDeactivation: settings.deactivationType,
      checkForCookieConsent: settings.checkForCookieConsent,
      cookieConsentProvider: settings.cookieConsentProvider,
      fields: { ...fields },
    };
  };

  return {
    settings,
    fields: readonly(fields),
    defaultFormWidth,
    embedCode,
    isTriggerValid,
    isActivationValid,
    isDeactivationValid,
    isValid,
    selectLayout,
    setAllowMinimize,
    setPosition,
    setLayoutText,
    setLayoutSelect,
    setNumericLayoutValue,
    commitDimension,
    setCornerValue,
    commitCornerValue,
    setTrigger,
    setActivation,
    setDeactivation,
    setCookieConsent,
    toStoredSettings,
  };
}

export type EmbedSettingsControls = ReturnType<typeof useEmbedSettings>;
